import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, map } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiResponse } from '../models/api-response.model';
import { AuthCheckResponse } from '../models/auth-check-response.model';

/**
 * Base API service providing core HTTP functionality and authentication.
 * Other services extend or inject this service for common operations.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected readonly http = inject(HttpClient);
  protected readonly configService = inject(ConfigService);

  protected get apiUrl(): string {
    return this.configService.apiUrl;
  }

  /**
   * Check if the user is authenticated by calling the auth check endpoint.
   * This call includes credentials (cookies) to verify authentication.
   * The cookie is expected to be set by a different URL/domain.
   */
  checkAuthentication(): Observable<AuthCheckResponse> {
    const url = `${this.apiUrl}${this.configService.authCheckEndpoint}`;
    
    return this.http.get<AuthCheckResponse>(url, {
      withCredentials: true
    }).pipe(
      map(response => ({
        ...response,
        isAuthenticated: response.isAuthenticated ?? false
      })),
      catchError(error => {
        console.error('Authentication check failed:', error);
        return of({
          isAuthenticated: false,
          message: error.status === 0 
            ? 'Unable to connect to the server. Please check your network connection.'
            : error.error?.message || 'Authentication failed. Please try again.'
        });
      })
    );
  }

  /**
   * Make a generic GET request to the API with credentials
   */
  get<T>(endpoint: string): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(`${this.apiUrl}${endpoint}`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        return of({
          success: false,
          error: error.error?.message || 'Request failed'
        });
      })
    );
  }

  /**
   * Make a generic POST request to the API with credentials
   */
  post<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http.post<ApiResponse<T>>(`${this.apiUrl}${endpoint}`, body, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        return of({
          success: false,
          error: error.error?.message || 'Request failed'
        });
      })
    );
  }
}
